// cSpell:ignore Silae mtpart cumul cumullignescontrat advancedVariable annee
import type {
  SilaeSourceFormData,
  AdvancedVariableParams as DefAdvancedVariableParams,
} from "@/types/silae";
import { cumulTypes } from "@/types/silae";

// AdvancedVariableParams from @/types/silae covers both use-cases (definition + runtime).
type RuntimeAdvancedVariableParams = DefAdvancedVariableParams;

import type {
  FormatterContext,
  SilaeFormatterAdvancedVariableParamsInput,
} from "./types";
import { resolveOptionLabel, resolveVariableLabel } from "./variables";

type FormatComplex = (
  expression: SilaeSourceFormData[],
  ctx: FormatterContext,
) => string;

interface FormatSourceOptions {
  formatComplex?: FormatComplex;
  /** Skip appending the bracketed "[Forcé via EVP: ...]" suffix — useful when
   *  the caller renders EVP forcing as a separate UI row. */
  skipEvpForce?: boolean;
}

// ----- Period helpers -----

/** Normalise une valeur scalaire (string | number | undefined) en string trimée.
 *  Tolère le legacy où certains champs typés `string` ont été persistés en `number`. */
function toTrimmedString(value: unknown): string {
  if (value === undefined || value === null) return "";
  return String(value).trim();
}

function applyPercentage(
  base: string,
  percentage: string | undefined,
  ctx: FormatterContext,
): string {
  const value = toTrimmedString(percentage) || "100";
  if (value === "100") return base;
  return ctx.t("step5Validation.formatters.connectives.percentageOf", {
    percent: value,
    base,
    defaultValue: "{{percent}} % de {{base}}",
  });
}

function applyAdditional(
  base: string,
  enabled: boolean | undefined,
  operator: string | undefined,
  value: string | undefined,
  ctx: FormatterContext,
): string {
  const normalized = toTrimmedString(value);
  if (!enabled || !normalized || normalized === "0") return base;
  const op = operator ?? "+";
  const operatorLabel = ctx.t(
    `step5Validation.formatters.mathOperators.${op}`,
    op,
  );
  return `${base} ${operatorLabel} ${normalized}`;
}

function applyEvpForce(
  base: string,
  source: SilaeSourceFormData,
  ctx: FormatterContext,
): string {
  if (!source.enableEvpForce || !source.evpForceTitle) return base;
  return `${base} [${ctx.t("step5Validation.evpForce.display", {
    title: source.evpForceTitle,
  })}]`;
}

// ----- Cumul bulletins -----

export function formatCumulBulletins(
  cumulPeriod: string,
  cumulType: string,
  customStartDate: string | undefined,
  customEndDate: string | undefined,
  cumulDivisor: string | undefined,
  ctx: FormatterContext,
): string {
  const baseLabel = ctx.t(
    "step5Validation.formatters.sources.cumulBulletins",
    "Cumul des bulletins",
  );
  const typeLabel = cumulType ? cumulTypes[cumulType] : "";

  const periodLabelText = cumulStockVarPeriodLabel(cumulPeriod, ctx);
  const rangeText = periodLabelText
    ? ctx.t("step5Validation.formatters.connectives.over", {
        period: periodLabelText,
        defaultValue: "sur {{period}}",
      })
    : cumulPeriod === "custom" && customStartDate && customEndDate
      ? ctx.t("step5Validation.formatters.connectives.fromTo", {
          from: formatSilaeDateExpression(customStartDate),
          to: formatSilaeDateExpression(customEndDate),
          defaultValue: "dans la période du {{from}} au {{to}}",
        })
      : "";

  let result = typeLabel ? `${baseLabel} « ${typeLabel} »` : baseLabel;

  if (rangeText) {
    result = `${result} (${rangeText})`;
  }

  if (cumulDivisor && cumulDivisor !== "1") {
    result = ctx.t("step5Validation.formatters.connectives.dividedBy", {
      base: result,
      divisor: cumulDivisor,
      defaultValue: "{{base}}, divisé par {{divisor}}",
    });
  }

  return result;
}

// ----- Cumul stock var -----

const CUMUL_STOCK_VAR_PERIOD_FALLBACKS: Record<string, string> = {
  year_current: "l'année en cours",
  year_previous: "l'année précédente",
  quarter_1: "le 1er trimestre",
  quarter_2: "le 2ème trimestre",
  quarter_3: "le 3ème trimestre",
  quarter_4: "le 4ème trimestre",
};

function cumulStockVarPeriodLabel(
  period: string | undefined,
  ctx: FormatterContext,
): string | null {
  if (!period || period === "custom") return null;
  const fallback = CUMUL_STOCK_VAR_PERIOD_FALLBACKS[period];
  if (!fallback) return null;
  return ctx.t(
    `step5Validation.formatters.cumulStockVarPeriods.${period}`,
    fallback,
  );
}

export function formatCumulStockVar(
  name: string,
  dateDebut: string,
  dateFin: string,
  percentage: string | undefined,
  enableAdditional: boolean | undefined,
  additionalOperator: string | undefined,
  additionalValue: string | undefined,
  ctx: FormatterContext,
  period?: string,
): string {
  if (!name || !dateDebut || !dateFin) {
    return ctx.t("step5Validation.cumulStockVar.notConfigured");
  }

  const baseLabel = ctx.t(
    "step5Validation.formatters.sources.cumulStockVar",
    "Cumul de variable stockée",
  );

  const periodLabel = cumulStockVarPeriodLabel(period, ctx);
  const rangeText = periodLabel
    ? ctx.t("step5Validation.formatters.connectives.over", {
        period: periodLabel,
        defaultValue: "sur {{period}}",
      })
    : ctx.t("step5Validation.formatters.connectives.fromTo", {
        from: formatSilaeDateExpression(dateDebut),
        to: formatSilaeDateExpression(dateFin),
        defaultValue: "dans la période du {{from}} au {{to}}",
      });

  let display = `${baseLabel} « ${name} » (${rangeText})`;

  display = applyPercentage(display, percentage, ctx);
  display = applyAdditional(
    display,
    enableAdditional,
    additionalOperator,
    additionalValue,
    ctx,
  );

  return display;
}

// ----- Questionnaire variable -----

const QUESTIONNAIRE_TYPE_LABEL_KEYS: Record<string, string> = {
  double: "double",
  int: "int",
  bool: "boolean",
  boolean: "boolean",
  date: "date",
  text: "text",
};

const QUESTIONNAIRE_TYPE_FALLBACKS: Record<string, string> = {
  double: "Décimal",
  int: "Entier",
  bool: "Booléen",
  boolean: "Booléen",
  date: "Date",
  text: "Texte",
};

const MONTH_NUMBER_LABELS: Record<string, string> = {
  "1": "janvier",
  "2": "février",
  "3": "mars",
  "4": "avril",
  "5": "mai",
  "6": "juin",
  "7": "juillet",
  "8": "août",
  "9": "septembre",
  "10": "octobre",
  "11": "novembre",
  "12": "décembre",
};

const MONTH_VARIABLE_LABELS: Record<string, string> = {
  "bul.mois": "mois du bulletin",
  "bul.mois-1": "mois précédant le bulletin",
  "bul.mois+1": "mois suivant le bulletin",
};

const YEAR_VARIABLE_LABELS: Record<string, string> = {
  "bul.annee": "année du bulletin",
  "bul.annee-1": "année précédant le bulletin",
  "bul.annee+1": "année suivant le bulletin",
};

function humanizeDayPart(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return "??";
  if (/^\d+$/.test(trimmed)) return trimmed.padStart(2, "0");
  return trimmed;
}

function humanizeMonthPart(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return "??";
  if (/^\d+$/.test(trimmed)) return trimmed.padStart(2, "0");
  if (MONTH_VARIABLE_LABELS[trimmed]) return MONTH_VARIABLE_LABELS[trimmed];
  if (MONTH_NUMBER_LABELS[trimmed]) return MONTH_NUMBER_LABELS[trimmed];
  let match = trimmed.match(/^bul\.mois-(\d+)$/);
  if (match) return `mois N-${match[1]} du bulletin`;
  match = trimmed.match(/^bul\.mois\+(\d+)$/);
  if (match) return `mois N+${match[1]} du bulletin`;
  return trimmed;
}

function humanizeYearPart(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return "????";
  if (/^\d{4}$/.test(trimmed)) return trimmed;
  if (/^\d+$/.test(trimmed)) return trimmed;
  if (YEAR_VARIABLE_LABELS[trimmed]) return YEAR_VARIABLE_LABELS[trimmed];
  let match = trimmed.match(/^bul\.annee-(\d+)$/);
  if (match) return `année N-${match[1]} du bulletin`;
  match = trimmed.match(/^bul\.annee\+(\d+)$/);
  if (match) return `année N+${match[1]} du bulletin`;
  return trimmed;
}

export function formatSilaeDateExpression(raw: string): string {
  const trimmed = raw.trim();
  const match = trimmed.match(/^Date\((.+)\)$/);
  if (!match) return trimmed;
  const parts = match[1].split(",").map((p) => p.trim());
  if (parts.length !== 3) return trimmed;
  const [day, month, year] = parts;
  return `${humanizeDayPart(day)}/${humanizeMonthPart(month)}/${humanizeYearPart(year)}`;
}

export function formatQuestionnaireValue(
  type: string,
  raw: string | undefined,
  ctx: FormatterContext,
): string {
  const value = (raw ?? "").trim();
  if (!value) {
    return ctx.t(
      "step5Validation.formatters.fallbacks.unsetValue",
      "valeur non définie",
    );
  }

  switch (type) {
    case "boolean":
    case "bool": {
      if (value === "true") {
        return ctx.t("step5Validation.formatters.boolean.true", "Vrai");
      }
      if (value === "false") {
        return ctx.t("step5Validation.formatters.boolean.false", "Faux");
      }
      return value;
    }
    case "date":
      return formatSilaeDateExpression(value);
    case "text":
      return `« ${value} »`;
    case "double":
    case "int":
    default:
      return value;
  }
}

export function formatQuestionnaireVariable(
  type: string,
  index: string,
  customLabel: string | undefined,
  percentage: string | undefined,
  ctx: FormatterContext,
): string {
  if (!type || !index) {
    return ctx.t("step5Validation.questionnaireVariable.notConfigured");
  }

  const typeLabel = ctx.t(
    `step5Validation.formatters.questionnaireTypes.${QUESTIONNAIRE_TYPE_LABEL_KEYS[type] ?? type}`,
    QUESTIONNAIRE_TYPE_FALLBACKS[type] ?? type,
  );

  const niceName = customLabel?.trim()
    ? customLabel.trim()
    : ctx.t("step5Validation.formatters.questionnaireField.indexedName", {
        type: typeLabel,
        index,
        defaultValue: "{{type}} n°{{index}}",
      });

  let display = ctx.t(
    "step5Validation.formatters.sources.questionnaireFieldWithType",
    {
      label: niceName,
      type: typeLabel,
      defaultValue: "Champ questionnaire « {{label}} » de type {{type}}",
    },
  );

  if (type === "double" || type === "int") {
    display = applyPercentage(display, percentage, ctx);
  }

  return display;
}

// ----- Advanced variable -----

function formatAdvancedParameterValue(
  paramDef: DefAdvancedVariableParams,
  userParam: SilaeFormatterAdvancedVariableParamsInput | undefined,
): string | null {
  if (paramDef.isFixed && paramDef.fixedValue) {
    return paramDef.fixedValue;
  }
  if (paramDef.isOutputParam) {
    const name =
      paramDef.outputVarName || userParam?.outputVarName || paramDef.id;
    return name.trim();
  }
  if (!userParam || !userParam.value) {
    return null;
  }

  let value = userParam.value;

  if (
    paramDef.type === "selection" &&
    paramDef.includeBulletinCourant &&
    userParam.shouldIncludeBulletinCourant
  ) {
    const numValue = parseInt(value);
    if (!isNaN(numValue)) {
      value = (numValue + 10).toString();
    }
  }

  if (paramDef.type === "selection") {
    return resolveOptionLabel(paramDef.options, value);
  }

  if (paramDef.type === "dynamicDate") {
    return formatSilaeDateExpression(value);
  }

  return value;
}

export function formatAdvancedVariable(
  variableCode: string,
  parameters: SilaeFormatterAdvancedVariableParamsInput[],
  percentage: string,
  globalEnableAdditional: boolean | undefined,
  globalAdditionalOperator: string | undefined,
  globalAdditionalValue: string | undefined,
  ctx: FormatterContext,
): string {
  const variable = ctx.findVariable(variableCode);
  if (!variable) return variableCode;

  const friendlyName = resolveVariableLabel(variable, variableCode);

  let result = friendlyName;

  if (
    variable.type === "function" &&
    variable.parameters &&
    parameters.length > 0
  ) {
    const variableParamDefs =
      variable.parameters as DefAdvancedVariableParams[];
    const userParamsFull =
      parameters as unknown as RuntimeAdvancedVariableParams[];

    const inputParts: string[] = [];
    const outputVarNames: string[] = [];

    variableParamDefs.forEach((paramDef) => {
      const userParam = userParamsFull.find((p) => p.id === paramDef.id) as
        | (SilaeFormatterAdvancedVariableParamsInput & {
            value?: string;
            outputVarName?: string;
          })
        | undefined;

      if (paramDef.isOutputParam) {
        // Variable de sortie — affichée séparément, pas comme paramètre d'entrée.
        const outName = (
          paramDef.outputVarName ||
          userParam?.outputVarName ||
          paramDef.id
        ).trim();
        if (outName) outputVarNames.push(outName);
        return;
      }

      const valueLabel = formatAdvancedParameterValue(paramDef, userParam);
      if (valueLabel === null) return;
      const paramLabel = (paramDef.label?.trim() || paramDef.id).replace(
        /:+\s*$/,
        "",
      );
      inputParts.push(`<m>${paramLabel}:</m> <b>${valueLabel}</b>`);
    });

    const baseName = friendlyName.replace(/\(\)\s*$/, "");
    const callStr =
      inputParts.length > 0
        ? `<b>${baseName}</b>\n${inputParts.join("\n")}`
        : `<b>${baseName}</b>()`;

    if (outputVarNames.length > 0) {
      // Affiche les variables de sortie après l'appel : "→ sortie: bs, ts, rp"
      result = `${callStr}\n<m>→ sortie:</m> <b>${outputVarNames.join(", ")}</b>`;
    } else {
      result = callStr;
    }

    result = applyPercentage(result, percentage, ctx);
  } else if (
    (variable.type === "int" || variable.type === "double") &&
    percentage !== "100"
  ) {
    result = applyPercentage(result, percentage, ctx);
  }

  result = applyAdditional(
    result,
    globalEnableAdditional,
    globalAdditionalOperator,
    globalAdditionalValue,
    ctx,
  );

  return result;
}

// ----- Main dispatcher -----

export function formatSource(
  source: SilaeSourceFormData,
  ctx: FormatterContext,
  options: FormatSourceOptions = {},
): string {
  let baseDisplay = "";

  switch (source.sourceType) {
    case "fixed":
      baseDisplay = ctx.t("step5Validation.formatters.sources.fixedAmount", {
        amount: `${source.amount ?? 0}`,
        defaultValue: "Montant fixe de {{amount}}",
      });
      break;
    case "evp":
      baseDisplay = ctx.t("step5Validation.formatters.sources.evpVariable", {
        title: source.evpTitle || "?",
        fallback: source.evpDefaultValue || "?",
        defaultValue: "Variable EVP « {{title}} » (par défaut : {{fallback}})",
      });
      break;
    case "salary_base":
      baseDisplay = applyPercentage(
        ctx.t(
          "step5Validation.formatters.sources.salaryBase",
          "Salaire de base",
        ),
        source.salaryPercentage,
        ctx,
      );
      break;
    case "salary_gross":
      baseDisplay = applyPercentage(
        ctx.t("step5Validation.formatters.sources.salaryBrut", "Salaire brut"),
        source.salaryPercentage,
        ctx,
      );
      break;
    case "salary_net":
      baseDisplay = applyPercentage(
        ctx.t("step5Validation.formatters.sources.salaryNet", "Salaire net"),
        source.salaryPercentage,
        ctx,
      );
      break;
    case "stored_variable":
      baseDisplay = ctx.t("step5Validation.formatters.sources.storedVariable", {
        name: source.storedVariableName || "?",
        defaultValue: "Variable stockée « {{name}} »",
      });
      break;
    case "manual_formula":
      baseDisplay = ctx.t("step5Validation.formatters.sources.manualFormula", {
        formula: source.manualFormula || "?",
        defaultValue: "Formule manuelle : {{formula}}",
      });
      break;
    case "cumul_bulletins":
      baseDisplay = formatCumulBulletins(
        source.cumulPeriod || "",
        source.cumulType || "",
        source.cumulCustomStartDate,
        source.cumulCustomEndDate,
        source.cumulDivisor,
        ctx,
      );
      break;
    case "cumul_stock_var":
      baseDisplay = formatCumulStockVar(
        source.cumulStockVarName || "?",
        source.cumulStockVarDateDebut || "?",
        source.cumulStockVarDateFin || "?",
        source.cumulStockVarPercentage,
        source.cumulStockVarEnableAdditional,
        source.cumulStockVarAdditionalOperator,
        source.cumulStockVarAdditionalValue,
        ctx,
        source.cumulStockVarPeriod,
      );
      break;
    case "advanced_variable": {
      baseDisplay = formatAdvancedVariable(
        source.advancedVariable || "",
        source.advancedVariableParameters as unknown as SilaeFormatterAdvancedVariableParamsInput[],
        source.advancedVariablePercentage || "100",
        source.advancedVariableEnableAdditional,
        source.advancedVariableAdditionalOperator,
        source.advancedVariableAdditionalValue,
        ctx,
      );
      const postCallVar = source.advancedVariablePostCallVarName?.trim();
      const postCallExpr = source.advancedVariablePostCallExpression?.trim();
      if (postCallVar && postCallExpr) {
        const postCallLabel = ctx.t(
          "step5Validation.formatters.sources.postCallLabel",
          {
            defaultValue: "valeur retenue",
          },
        );
        baseDisplay += `\n<m>→ ${postCallLabel} :</m> <pc>${postCallVar} = ${postCallExpr}</pc>`;
      }
      break;
    }
    case "questionnaire_variable":
      baseDisplay = formatQuestionnaireVariable(
        source.questionnaireVariableType || "",
        source.questionnaireVariableIndex || "",
        source.questionnaireVariableLabel,
        source.questionnaireVariablePercentage,
        ctx,
      );
      break;
    case "complex_expression":
      baseDisplay = options.formatComplex
        ? options.formatComplex(source.complexExpression || [], ctx)
        : "";
      break;
    default:
      baseDisplay = ctx.t("step5Validation.notDefined", "Non définie");
  }

  if (options.skipEvpForce) return baseDisplay;
  return applyEvpForce(baseDisplay, source, ctx);
}
